import { isFulfilled } from "@reduxjs/toolkit";
import type { AppDispatch } from "../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../store/application/slice";
import {
  getProfileDetailsThunk,
  myProfileStageSubmit,
} from "../store/onboarding/thunk";

/**
 * Check current media permissions for camera and microphone
 */
export const checkMediaPermissions = async () => {
  try {
    if (navigator.permissions) {
      try {
        const cameraPermission = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        const microphonePermission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        return {
          camera: cameraPermission.state,
          microphone: microphonePermission.state,
        };
      } catch (err) {
        console.warn(
          "Permissions.query for camera/microphone failed, falling back:",
          err
        );
      }
    }

    // Fallback: derive permission-like state from device labels
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideo = devices.some((d) => d.kind === "videoinput");
      const hasAudio = devices.some((d) => d.kind === "audioinput");
      const videoWithLabel = devices.some(
        (d) => d.kind === "videoinput" && d.label
      );
      const audioWithLabel = devices.some(
        (d) => d.kind === "audioinput" && d.label
      );

      return {
        camera: videoWithLabel
          ? ("granted" as PermissionState)
          : hasVideo
          ? ("prompt" as PermissionState)
          : ("denied" as PermissionState),
        microphone: audioWithLabel
          ? ("granted" as PermissionState)
          : hasAudio
          ? ("prompt" as PermissionState)
          : ("denied" as PermissionState),
      };
    }
  } catch (error) {
    console.warn("Permission detection failed:", error);
  }
  return { camera: null, microphone: null };
};

/**
 * Setup permission monitoring for camera and microphone
 * Returns cleanup function to be called on unmount
 */
export const setupPermissionMonitoring = async (
  iframeUrl: string | null,
  initialPermissions: {
    camera: PermissionState | null;
    microphone: PermissionState | null;
  },
  setInitialPermissions: (perms: {
    camera: PermissionState | null;
    microphone: PermissionState | null;
  }) => void,
  setShowRefreshMessage: (show: boolean) => void
): Promise<() => void> => {
  if (!iframeUrl) {
    return () => {};
  }

  const initialPerms = await checkMediaPermissions();
  setInitialPermissions(initialPerms);

  let permissionListeners: (() => void)[] = [];
  let pollingInterval: number | undefined;

  // ✅ Handle browsers that support permission change events
  if (navigator.permissions) {
    try {
      const cameraPermission = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      const microphonePermission = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });

      const handlePermissionChange = async () => {
        const currentPerms = await checkMediaPermissions();
        const cameraChanged =
          initialPerms.camera !== "granted" && currentPerms.camera === "granted";
        const microphoneChanged =
          initialPerms.microphone !== "granted" &&
          currentPerms.microphone === "granted";
        if (cameraChanged || microphoneChanged) {
          setShowRefreshMessage(true);
        }
      };

      cameraPermission.addEventListener("change", handlePermissionChange);
      microphonePermission.addEventListener("change", handlePermissionChange);

      permissionListeners.push(
        () =>
          cameraPermission.removeEventListener("change", handlePermissionChange),
        () =>
          microphonePermission.removeEventListener(
            "change",
            handlePermissionChange
          )
      );

      // Return cleanup function
      return () => {
        permissionListeners.forEach((cleanup) => cleanup());
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
      };
    } catch (err) {
      console.warn(
        "Permissions API change-listener setup failed, falling back to polling:",
        err
      );
    }
  }

  // ✅ Fallback: Poll device labels every 2s
  try {
    const derive = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoWithLabel = devices.some(
          (d) => d.kind === "videoinput" && d.label
        );
        const audioWithLabel = devices.some(
          (d) => d.kind === "audioinput" && d.label
        );
        return {
          camera: videoWithLabel ? "granted" : "prompt",
          microphone: audioWithLabel ? "granted" : "prompt",
        };
      } catch (err) {
        console.warn("derive() failed:", err);
        return { camera: null, microphone: null };
      }
    };

    pollingInterval = window.setInterval(async () => {
      const current = await derive();
      const cameraChanged =
        initialPerms.camera !== "granted" && current.camera === "granted";
      const microphoneChanged =
        initialPerms.microphone !== "granted" &&
        current.microphone === "granted";
      if (cameraChanged || microphoneChanged) {
        console.log("Detected permission grant via polling ✅");
        setShowRefreshMessage(true);

        if (pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = undefined;
        }
      }
    }, 2000);
  } catch (error) {
    console.warn("Polling-based permission monitoring failed:", error);
  }

  // Return cleanup function
  return () => {
    permissionListeners.forEach((cleanup) => cleanup());
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
  };
};

/**
 * Handle submission of stage 1
 */
export const handleSubmitStage = async (
  dispatch: AppDispatch,
  stage: number
) => {
  dispatch(showTransparentPreloaderAction());
  const payload = new FormData();
  payload.append("stage", stage.toString());
  const myprofileStageResponse = await dispatch(myProfileStageSubmit(payload));
  await dispatch(getProfileDetailsThunk());
  dispatch(hideTransparentPreloaderAction());
  if (isFulfilled(myprofileStageResponse)) {
    dispatch(showNotificationAction(`Stage ${stage} Resubmitted`));
  }
};
